import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { first, Observable, of } from 'rxjs';
import { Location } from '@data/matching-session/matching-session.model';
import { Logger } from '@shared/util/logger.util';

function stripCountry(address: string) {
  if (address.endsWith('Deutschland')) {
    return address.substring(0, address.lastIndexOf(','));
  }
  return address;
}

function createLocationFromSearchResult(address: Feature): Location {
  return {
    address: stripCountry(address.place_name),
    region: address.context[3]?.text ?? 'Deutschland',
    location: {
      type: 'Point',
      coordinates: [+address.center[0], +address.center[1]],
    },
  };
}

export interface MapboxOutput {
  attribution: string;
  features: Feature[];
  query: string[];
}

export interface MapboxContext {
  id: string;
  text: string;
  short_code?: string;
  wikidata?: string;
}

export interface Feature {
  place_name: string;
  context: MapboxContext[];
  center: number[];
}

@Injectable({
  providedIn: 'root',
})
export class GeocoderService {
  private http = inject(HttpClient);

  private geocodeUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places/';

  public searchAddress(query: string, type: string = 'address'): Observable<Location[]> {
    const types = type === 'place' ? 'place,postcode' : type;
    const url: string =
      this.geocodeUrl +
      encodeURIComponent(query) +
      `.json?country=DE&language=DE&types=${types}&limit=7&access_token=` +
      environment.mapbox.apiKey;
    return this.http.get<MapboxOutput>(url).pipe(
      map((res) => res.features ?? []),
      map((features) => features.map((address) => createLocationFromSearchResult(address))),
      first(),
      catchError((error) => {
        Logger.error(error);
        return of<Location[]>([]);
      }),
    );
  }
}
